/**
 * ActivityIcon (CONTRACTS.md §1). Resolves a catalogue iconId to an original
 * 24×24 line-icon SVG (stroke 1.8, round caps, currentColor). Unknown ids
 * render a neutral dot — never a broken image.
 *
 * `showWarningBadge` adds the small red exclamation badge used by
 * "WhatsApp bekijken". The badge is a separate accessible status mark: a
 * real "!" glyph plus visually-hidden Dutch text — never color alone.
 */
import { IconSvg } from '@/components/icons/IconSvg'
import type { IconProps } from '@/components/icons/IconSvg'
import { ACTIVITY_GLYPHS, FALLBACK_GLYPH_ID } from './glyphs'
import './activity-icon.css'

export type ActivityIconProps = IconProps & {
  /** Catalogue iconId (Activity.iconId). Unknown ids fall back to a dot. */
  iconId: string
  /** Renders the warning badge variant (Activity.badge === 'warning'). */
  showWarningBadge?: boolean
  /**
   * Accessible Dutch text for the warning badge. Only change when the
   * meaning of the badge changes; the icon itself stays decorative.
   */
  warningLabel?: string
}

export function ActivityIcon({
  iconId,
  size = 24,
  className,
  showWarningBadge = false,
  warningLabel = 'Let op: deze activiteit kan veel schermtijd met zich meebrengen.',
}: ActivityIconProps) {
  const glyph = ACTIVITY_GLYPHS[iconId] ?? ACTIVITY_GLYPHS[FALLBACK_GLYPH_ID]
  if (!showWarningBadge) {
    return <IconSvg size={size} className={className}>{glyph}</IconSvg>
  }
  return (
    <span className="activity-icon-badged">
      <IconSvg size={size} className={className}>{glyph}</IconSvg>
      <span className="activity-icon-badge" aria-hidden="true">
        <svg
          width={Math.max(10, Math.round(size * 0.55))}
          height={Math.max(10, Math.round(size * 0.55))}
          viewBox="0 0 12 12"
          fill="none"
          focusable="false"
        >
          <circle cx="6" cy="6" r="5.4" fill="var(--danger, #bf5a64)" stroke="var(--surface, #fff)" strokeWidth="1.2" />
          <path d="M6 3v3.4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="6" cy="8.7" r="0.9" fill="#fff" />
        </svg>
      </span>
      <span className="visually-hidden">{warningLabel}</span>
    </span>
  )
}

/** True when the iconId exists in the glyph registry. */
export function hasActivityGlyph(iconId: string): boolean {
  return iconId in ACTIVITY_GLYPHS
}
